import {RefObject, useEffect, useRef} from 'react';

/**
 * Đóng menu/popover khi bấm ra ngoài nó.
 *
 * Nghe `mousedown` chứ không phải `click`: nếu nghe `click` thì một cú bấm vào
 * nút bên ngoài sẽ chạy handler này TRƯỚC khi nút đó xử lý, và trên vài trình
 * duyệt phần tử bị gỡ khỏi DOM giữa chừng làm mất luôn cú bấm.
 *
 * Chỉ gắn listener khi đang mở - bốn menu trong app cùng nghe document thì
 * không cần chúng cùng chạy khi tất cả đều đang đóng.
 */
export const useClickOutside = (
    ref: RefObject<HTMLElement>,
    isOpen: boolean,
    onClose: () => void,
) => {
    // Giữ `onClose` trong ref: nơi gọi hầu hết truyền arrow function tạo mới sau
    // mỗi lần render. Nếu để nó trong deps của effect thì listener bị gỡ ra gắn
    // lại liên tục; nếu bỏ khỏi deps thì effect giữ mãi bản đầu tiên. Ref tránh
    // được cả hai - listener gắn đúng một lần, mà vẫn luôn gọi bản mới nhất.
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    useEffect(() => {
        if (!isOpen) return;

        const onPointerDown = (event: MouseEvent) => {
            if (!ref.current?.contains(event.target as Node)) onCloseRef.current();
        };

        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
    }, [ref, isOpen]);
};
